import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Loader from "@amzn/meridian/loader";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { ALERT_DEFAULT_MESSAGE, ALERT_AUTHENTICATION_MESSAGE, ALERT_AUDIT_LOAD_MESSAGE, PLAN_GROUPS_LOAD_ERROR_MESSAGE, OVERALL_SEGMENT_ID } from "../../constants";
import ContentLayout from "../layout/ContentLayout";
import AlertErrorWithMessage from "../alert/AlertErrorWithMessage";
import AuditStatsRow from "./AuditStatsRow";
import ShiftStatusBadge from "./ShiftStatusBadge";
import SegmentTabs from "./SegmentTabs";
import { offsetMinutesFromIso } from "./segmentTime";
import AuditActionGroup from "./AuditActionGroup";
import AuditDetailsTable from "./AuditDetailsTable";
import OverallAuditTab from "./OverallAuditTab";
import RosterSelect from "./RosterSelect";
import PlanGroupSelect, { defaultPlanGroupIdForDate, defaultPlanGroupOnVisit } from "./PlanGroupSelect";
import AdminApiFactory from "../../admin-api/AdminApiFactory";
import AdminContext from "../../store/admin-context";
import { useRouteBusinessSpaceSync } from "../../store/useRouteBusinessSpaceSync";
import AuditContext from "../../store/audit-store";
import { AuditDetails, ListPlanGroupsResponse, PlanGroupSummary } from "../../admin-api/generated-src";

export default function AuditPage() {
  const adminContext = useContext(AdminContext);
  const auditContext = useContext(AuditContext);
  const { selectedNode, selectedBusinessSpace } = adminContext;
  const { isAuditTableLoading,
    auditRecordList,
    currentRosterId,
    flipRefresh,
    planGroups,
    selectedPlanGroupId,
    selectedSegmentId,
    setCurrentRosterId,
    setIsAuditTableLoading,
    setRosterIdList,
    setAuditRecordList,
    setAuditStats,
    setPlanGroups,
    setSelectedPlanGroupId,
    setSelectedSegmentId } = auditContext;
  const { nodeId } = useParams<{ nodeId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  // Starts true so a stored business space can't flash the legacy fallback before the first load.
  const [isPlanGroupsLoading, setIsPlanGroupsLoading] = useState(true);
  const [planGroupsLoadError, setPlanGroupsLoadError] = useState(false);
  // Set only on a real listPlanGroups resolve (not the empty-businessSpace early-return), so an
  // empty list reads as legacy only after an actual load.
  const [planGroupsLoaded, setPlanGroupsLoaded] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState(ALERT_DEFAULT_MESSAGE);
  const [hasPermission, setHasPermission] = useState(true);

  const AdminApi = AdminApiFactory();
  const prevBusinessSpaceRef = useRef(selectedBusinessSpace);

  // Node with no plan groups at all: fall back to the standalone legacy roster picker.
  const isLegacyMode =
    !!selectedBusinessSpace &&
    planGroupsLoaded &&
    !planGroupsLoadError &&
    planGroups.length === 0;

  const selectedPlanGroup: PlanGroupSummary | undefined = planGroups.find(
    (pg) => pg.planGroupId === selectedPlanGroupId
  );
  // CH-vs-legacy is a per-shift property: a selected shift with no segments is a legacy roster and
  // renders as a single table; one with segments is a CH plan group and renders segment tabs. A node
  // mid-transition legitimately has both, so this is decided per selection, not for the whole page.
  const isLegacyShift = !!selectedPlanGroup && (selectedPlanGroup.segments?.length ?? 0) === 0;
  // A single-segment CH shift renders flat (no tabs) but must stay on the per-segment data path —
  // its scan records live in the nodeId:segmentId DDB partition, not under the planGroupId.
  const singleSegment =
    (selectedPlanGroup?.segments?.length ?? 0) === 1 ? selectedPlanGroup!.segments![0] : undefined;
  const isSingleSegmentShift = !!singleSegment?.segmentId;
  // Facility UTC offset for segment-time rendering, derived from the plan group's own startTime
  // (e.g. "…-04:00"). It's the offset on that shift date, so it's DST-correct; null → UTC.
  const segmentOffsetMinutes = offsetMinutesFromIso(selectedPlanGroup?.startTime);

  useRouteBusinessSpaceSync();

  // Pin the lone segment as the selection; re-fires after every reset-to-overall so the pin wins.
  useEffect(() => {
    if (isSingleSegmentShift && selectedSegmentId !== singleSegment!.segmentId) {
      setSelectedSegmentId(singleSegment!.segmentId!);
    }
  }, [isSingleSegmentShift, singleSegment?.segmentId, selectedSegmentId]);

  const cleanup = () => {
    setIsLoading(false);
    setHasPermission(true);
    setAlertMessage(ALERT_DEFAULT_MESSAGE);
    setShowAlert(false);
    setRosterIdList([]);
    setAuditRecordList([]);
    setAuditStats({});
    setPlanGroups([]);
    setPlanGroupsLoadError(false);
    setSelectedPlanGroupId(undefined);
  }

  // Fetch plan groups for the dropdown
  useEffect(() => {
    // Nothing to load yet; clear the initially-true loading flag so the picker doesn't spin.
    if (!nodeId || !selectedBusinessSpace) {
      setIsPlanGroupsLoading(false);
      setPlanGroupsLoaded(false);
      return;
    }

    let active = true;
    setIsPlanGroupsLoading(true);
    setPlanGroupsLoadError(false);
    setPlanGroupsLoaded(false);
    (async () => {
      try {
        const response: ListPlanGroupsResponse = (await AdminApi.listPlanGroups(selectedBusinessSpace, nodeId)).data as ListPlanGroupsResponse;
        if (!active) return;
        const groups = response?.planGroups || [];
        setPlanGroups(groups);
        setPlanGroupsLoaded(true);
        // Preserve the user's selection across a refresh (the timer and the refresh button both
        // re-run this effect via flipRefresh). Only (re)initialize when the current selection is
        // no longer valid — first load, or the selected plan group is absent from the new list
        // because the node / business space changed
        const selectionStillValid = groups.some((g) => g.planGroupId === selectedPlanGroupId);
        if (groups.length === 0) {
          setSelectedPlanGroupId(undefined);
        } else if (!selectionStillValid) {
          // Default selection by operational priority: the In-Progress shift, else the nearest
          // upcoming Scheduled, else the most recent past Completed (defaultPlanGroupOnVisit); fall
          // back to the first plan group when none match. In every case resolve to the current
          // (highest-version) plan group of that shift via defaultPlanGroupIdForDate so an
          // Overridden version is never the initial selection regardless of backend ordering.
          const anchor = defaultPlanGroupOnVisit(groups) ?? groups[0];
          setSelectedPlanGroupId(
            defaultPlanGroupIdForDate(groups, anchor.shiftDate, anchor.shiftType) ?? anchor.planGroupId
          );
          setSelectedSegmentId(OVERALL_SEGMENT_ID);
        }
      } catch (ex: any) {
        if (!active) return;
        console.error(`Error fetching plan groups for ${nodeId}`, ex);
        setPlanGroupsLoadError(true);
        if (ex.response && ex.response.status === 403) {
          setHasPermission(false);
          setAlertMessage(ALERT_AUTHENTICATION_MESSAGE);
          setShowAlert(true);
        }
      } finally {
        if (active) {
          setIsPlanGroupsLoading(false);
        }
      }
    })();

    return () => { active = false; };
  }, [nodeId, selectedBusinessSpace, flipRefresh]);

  // Legacy node with no plan groups at all — auto-load the current shift on mount; RosterSelect then
  // drives switching between rosters.
  useEffect(() => {
    if (!isLegacyMode) {
      return;
    }

    let rosterId = currentRosterId;
    if (prevBusinessSpaceRef.current !== selectedBusinessSpace) {
      rosterId = undefined;
      setCurrentRosterId(undefined);
      prevBusinessSpaceRef.current = selectedBusinessSpace;
    }

    (async () => {
      setIsLoading(true);
      try {
        const auditDetailsResponse: AuditDetails = (await AdminApi.getAuditDetailsForCurrentShift(nodeId, rosterId, selectedBusinessSpace || undefined)).data as AuditDetails;
        if (auditDetailsResponse) {
          const { currRosterId = "",
            NodeRosterIdMappingInfo = [],
            AuditRecord = [],
            AuditStats = {} } = auditDetailsResponse;
          if (rosterId === undefined) {
            setCurrentRosterId(formatCurrentRosterId(currRosterId));
          }
          setRosterIdList(NodeRosterIdMappingInfo);
          setAuditRecordList(AuditRecord);
          setAuditStats(AuditStats);
          setShowAlert(false);
        } else {
          console.error(`Got empty response from /audit/${selectedNode}`)
        }
      } catch (ex: any) {
        setShowAlert(true);
        console.error(`Error fetching /audit/${selectedNode}`, ex);
        if (ex.response && ex.response.status === 403) {
          setHasPermission(false);
          setAlertMessage(ALERT_AUTHENTICATION_MESSAGE);
        } else {
          setAlertMessage(ALERT_AUDIT_LOAD_MESSAGE);
        }
      } finally {
        setIsLoading(false);
      }
    })();

    return cleanup;
  }, [flipRefresh, selectedBusinessSpace, isLegacyMode, nodeId]);

  // Selected legacy shift (a plan group with no segments) — fetch its single roster's audit data by
  // planGroupId. CH shifts load via the overall/segment fetches instead.
  useEffect(() => {
    if (!isLegacyShift || !selectedPlanGroupId) {
      return;
    }

    let active = true;
    setIsAuditTableLoading(true);
    (async () => {
      try {
        const auditDetailsResponse: AuditDetails = (await AdminApi.getAuditDetailsForCurrentShift(nodeId, selectedPlanGroupId, selectedBusinessSpace || undefined)).data as AuditDetails;
        if (!active) return;
        if (auditDetailsResponse) {
          const { AuditRecord = [], AuditStats = {} } = auditDetailsResponse;
          setCurrentRosterId(selectedPlanGroupId);
          setAuditRecordList(AuditRecord);
          setAuditStats(AuditStats);
          setShowAlert(false);
        } else {
          console.error(`Got empty response from /audit/${selectedNode}`)
        }
      } catch (ex: any) {
        if (!active) return;
        setShowAlert(true);
        console.error(`Error fetching /audit/${selectedNode}`, ex);
        if (ex.response && ex.response.status === 403) {
          setHasPermission(false);
          setAlertMessage(ALERT_AUTHENTICATION_MESSAGE);
        } else {
          setAlertMessage(ALERT_AUDIT_LOAD_MESSAGE);
        }
      } finally {
        if (active) {
          setIsAuditTableLoading(false);
        }
      }
    })();

    return () => { active = false; };
  }, [isLegacyShift, selectedPlanGroupId, selectedBusinessSpace, flipRefresh, nodeId]);

  function formatCurrentRosterId(currentRosterId) {
    const currentRosterIdSplitStr = currentRosterId.split(":");
    if (currentRosterIdSplitStr.length === 2) {
      return currentRosterIdSplitStr[1];
    } else {
      return "";
    }
  }

  // Per-segment audit fetch — fires when a non-overall segment tab is selected
  useEffect(() => {
    if (!selectedPlanGroupId || selectedSegmentId === OVERALL_SEGMENT_ID) return;

    let active = true;
    setIsAuditTableLoading(true);

    (async () => {
      try {
        const auditDetailsResponse: AuditDetails = (await AdminApi.getAuditDetailsForCurrentShift(nodeId, selectedSegmentId, selectedBusinessSpace || undefined)).data as AuditDetails;
        if (!active) return;
        if (auditDetailsResponse) {
          const { NodeRosterIdMappingInfo = [], AuditRecord = [], AuditStats = {} } = auditDetailsResponse;
          setCurrentRosterId(selectedSegmentId);
          setRosterIdList(NodeRosterIdMappingInfo);
          setAuditRecordList(AuditRecord);
          setAuditStats(AuditStats);
        } else {
          console.error(`Got empty response from /audit/${selectedNode} for segment ${selectedSegmentId}`);
        }
      } catch (ex: any) {
        if (!active) return;
        setShowAlert(true);
        console.error(`Error fetching /audit/${selectedNode} for segment ${selectedSegmentId}`, ex);
        if (ex.response && ex.response.status === 403) {
          setHasPermission(false);
          setAlertMessage(ALERT_AUTHENTICATION_MESSAGE);
        }
      } finally {
        if (active) {
          setIsAuditTableLoading(false);
        }
      }
    })();

    return () => { active = false; };
  }, [selectedSegmentId, selectedPlanGroupId, selectedBusinessSpace, flipRefresh]);

  return <ContentLayout nodeId={nodeId} helperText="">
    {showAlert && <AlertErrorWithMessage message={alertMessage} />}
    {isLoading ? <Loader /> : hasPermission && <Column width="100%">
      <Row alignmentHorizontal="justify">
        {isPlanGroupsLoading ? (
          <Loader size="small" />
        ) : planGroupsLoadError ? (
          <Text>{PLAN_GROUPS_LOAD_ERROR_MESSAGE}</Text>
        ) : planGroups.length > 0 ? (
          <PlanGroupSelect />
        ) : isLegacyMode ? (
          <RosterSelect />
        ) : null}
        <AuditActionGroup />
      </Row>
      {/* CH shift (has segments) -> segment tabs; legacy and single-segment shifts render no tabs. */}
      {selectedPlanGroup && !isLegacyShift && !isSingleSegmentShift && (
        <SegmentTabs
          segments={selectedPlanGroup.segments || []}
          selectedSegmentId={selectedSegmentId}
          onTabChange={setSelectedSegmentId}
          offsetMinutes={segmentOffsetMinutes}
        />
      )}
      {isLegacyShift || isLegacyMode ? (
        // Selected legacy shift (no segments), or a node with no plan groups at all: single roster table.
        isAuditTableLoading ? <Loader /> : <Column>
          <AuditStatsRow />
          <AuditDetailsTable auditRecordList={auditRecordList} />
        </Column>
      ) : selectedPlanGroup ? (
        // CH shift: Overall tab owns its own loading state via useOverallAuditData, so it is rendered
        // OUTSIDE the isAuditTableLoading gate (that flag is owned by the per-segment fetch effect and
        // can stick true across a mid-load tab switch).
        selectedSegmentId === OVERALL_SEGMENT_ID && !isSingleSegmentShift
          ? <Column>
              {/* The Overall tab aggregates segments and has no per-roster status of its own, so the
                  plan-group shift status (e.g. Overridden) is surfaced here to match the per-segment
                  and legacy views. */}
              <ShiftStatusBadge />
              <OverallAuditTab
                nodeId={nodeId}
                planGroupId={selectedPlanGroupId}
                businessSpace={selectedBusinessSpace || undefined}
                refreshToken={flipRefresh}
                offsetMinutes={segmentOffsetMinutes}
              />
            </Column>
          : (isAuditTableLoading || selectedSegmentId === OVERALL_SEGMENT_ID ? <Loader /> : <Column>
              <AuditStatsRow />
              <AuditDetailsTable key={selectedSegmentId} auditRecordList={auditRecordList} />
            </Column>)
      ) : null}
    </Column>}
  </ContentLayout>
}
